import { parseLocalDate } from './dateUtils'
import type { FinanceData } from './intelligenceEngine'

export type MoneyRangeId = '7d' | '30d' | '90d' | '6m'

export function moneyRangeDays(id: MoneyRangeId): number {
  switch (id) {
    case '7d':
      return 7
    case '30d':
      return 30
    case '90d':
      return 90
    case '6m':
      return 183
    default:
      return 30
  }
}

export interface MoneyPeriodSummary {
  startingBalance: number
  moneyIn: number
  moneyOut: number
  net: number
}

/**
 * Dashboard uses forward-looking cash flow windows (matching Money page):
 * Period = [today, today + range - 1 day].
 * Starting balance comes from caller (persisted Money balance), not historical rollup.
 */
export function summarizeMoneyPeriod(
  finances: FinanceData[],
  range: MoneyRangeId,
  startingBalance = 0,
): MoneyPeriodSummary {
  const { start, end } = getMoneyPeriodBounds(range)
  const startTime = start.getTime()
  const endTime = end.getTime()

  const effectiveTime = (f: FinanceData) => {
    const dateStr = f.type === 'spend' && f.due_date ? f.due_date : f.date
    return parseLocalDate(dateStr)?.getTime() ?? 0
  }

  let moneyIn = 0
  let moneyOut = 0

  for (const f of finances) {
    const t = effectiveTime(f)
    if (!t) continue
    const amt = Number(f.amount) || 0
    if (t >= startTime && t <= endTime) {
      if (f.type === 'revenue') moneyIn += amt
      else moneyOut += amt
    }
  }

  return {
    startingBalance,
    moneyIn,
    moneyOut,
    net: moneyIn - moneyOut,
  }
}

export function getMoneyPeriodBounds(range: MoneyRangeId): { start: Date; end: Date } {
  const days = moneyRangeDays(range)
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + (days - 1))
  end.setHours(23, 59, 59, 999)
  return { start, end }
}

/** Transactions in the selected period, newest first. */
export function listFinancesInPeriod(finances: FinanceData[], range: MoneyRangeId): FinanceData[] {
  const { start, end } = getMoneyPeriodBounds(range)
  const startTime = start.getTime()
  const endTime = end.getTime()
  return finances
    .filter((f) => {
      const dateStr = f.type === 'spend' && f.due_date ? f.due_date : f.date
      const t = parseLocalDate(dateStr)?.getTime() ?? 0
      return t >= startTime && t <= endTime
    })
    .sort((a, b) => {
      const ad = a.type === 'spend' && a.due_date ? a.due_date : a.date
      const bd = b.type === 'spend' && b.due_date ? b.due_date : b.date
      return (parseLocalDate(bd)?.getTime() ?? 0) - (parseLocalDate(ad)?.getTime() ?? 0)
    })
}
